const count = (n) => Number.isSafeInteger(n) && n >= 0;
const text = (value) => value == null || typeof value === 'string';
const money = (value) =>
  value === null || (typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value));
const date = (value) =>
  value == null ||
  (typeof value === 'string' &&
    /(Z|[+-]\d{2}:\d{2})$/.test(value) &&
    !Number.isNaN(Date.parse(value)));
export function validSummary(value) {
  return (
    !!value &&
    ['totalCount', 'pendingCount', 'completedCount'].every((key) => count(value[key])) &&
    value.pendingCount + value.completedCount === value.totalCount &&
    value.state ===
      (value.totalCount === 0 ? 'NONE' : value.pendingCount > 0 ? 'PENDING' : 'COMPLETED')
  );
}
export function occurrenceHint(summary) {
  if (!validSummary(summary)) return 'Ocorrências não consultadas';
  return summary.state === 'NONE'
    ? 'Sem ocorrências'
    : summary.state === 'COMPLETED'
      ? 'Ocorrências concluídas'
      : `${summary.pendingCount} ocorrência(s) pendente(s)`;
}
export function validateOccurrences(data, travelId, page) {
  const fail = () => {
    throw new Error('Resposta inválida de ocorrências. Tente novamente.');
  };
  if (
    data?.version !== 1 ||
    data.travelId !== travelId ||
    !text(data.travelNumber) ||
    !data.asOf ||
    !date(data.asOf) ||
    !validSummary(data.summary) ||
    !Array.isArray(data.data)
  )
    fail();
  const pagination = data.pagination;
  if (
    pagination?.page !== page ||
    pagination.perPage !== 50 ||
    pagination.totalPages !== Math.ceil(data.summary.totalCount / 50) ||
    pagination.hasMore !== page * 50 < data.summary.totalCount ||
    data.data.length !== Math.min(50, Math.max(0, data.summary.totalCount - (page - 1) * 50))
  )
    fail();
  const ids = new Set();
  for (const row of data.data) {
    if (
      !row ||
      !count(row.id) ||
      row.id === 0 ||
      ids.has(row.id) ||
      typeof row.pending !== 'boolean' ||
      !['type', 'description', 'responsible', 'concludedBy', 'createdBy', 'customerNumber'].every(
        (key) => text(row[key]),
      ) ||
      !['startedAt', 'endedAt', 'updatedAt', 'createdAt'].every((key) => date(row[key])) ||
      !Array.isArray(row.groups) ||
      !row.groups.every((group) => typeof group === 'string') ||
      !['payCarrier', 'receiverClient', 'retainedBranch'].every((key) => money(row[key])) ||
      !count(row.attachmentCount)
    )
      fail();
    ids.add(row.id);
  }
  const pending = data.data.filter((row) => row.pending).length;
  if (
    pending > data.summary.pendingCount ||
    data.data.length - pending > data.summary.completedCount
  )
    fail();
  return data;
}

/** Valor monetário do servidor ('10000.00') em reais; ausente vira "Não informado". */
export function formatOccurrenceMoney(value) {
  if (!money(value) || value === null) return 'Não informado';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(value),
  );
}

/** Soma valores do servidor ('10000.00') em centavos, sem erro de ponto flutuante; nulos ficam de fora. */
export function sumOccurrenceMoney(values) {
  const cents = values
    .filter((value) => value !== null && money(value))
    .reduce((sum, value) => sum + Number(value.replace('.', '')), 0);
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * Anexos de `occurrence-media/:occurrenceId` (os mesmos da tela da viagem): só nome e URL https
 * assinada, que vale 1 h a partir desta consulta. Itens sem URL válida ficam de fora.
 */
export function readOccurrenceAttachments(data) {
  if (!Array.isArray(data)) throw new Error('Resposta inválida de anexos. Tente novamente.');
  return data
    .map((item) => ({
      id: item?.id ?? item?.media?.id,
      name: item?.media?.description || item?.media?.name || 'Anexo',
      url: item?.media?.full_path,
    }))
    .filter(
      (item) =>
        (typeof item.id === 'number' || typeof item.id === 'string') &&
        typeof item.url === 'string' &&
        /^https:\/\//i.test(item.url),
    );
}

/** Números nacionais usam Brasil; DDI explícito é preservado. Não extrai dígitos de texto arbitrário. */
export function whatsappContact(value) {
  if (typeof value !== 'string' || !value.trim()) return { display: null, href: null };
  const raw = value.trim();
  if (!/^\+?[\d\s().-]+$/.test(raw)) return { display: raw, href: null };
  let digits = raw.replace(/\D/g, '');
  const international = raw.startsWith('+') || digits.startsWith('00');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (!international && /^\d{10,11}$/.test(digits)) digits = `55${digits}`;
  else if (!international && !/^55\d{10,11}$/.test(digits)) return { display: raw, href: null };
  if (!/^[1-9]\d{7,14}$/.test(digits)) return { display: raw, href: null };
  if (digits.startsWith('55') && !/^55[1-9]\d[2-9]\d{7,8}$/.test(digits))
    return { display: raw, href: null };
  const display = digits.startsWith('55')
    ? `+55 (${digits.slice(2, 4)}) ${digits.slice(4, -4)}-${digits.slice(-4)}`
    : `+${digits}`;
  return { display, href: `https://wa.me/${digits}` };
}
